/**
 * id chips：`、`-joined id 列表（launch_url 渠道 id、special_condition 的 excluded_custom ids）
 * 以 chips 录入与展示，支持增删（Enter 或逗号提交）。
 * 可选 disabled 只读态：隐藏 × 与「添加」、输入框 readOnly、commit/remove 兜底拦截
 * （跨域复用：腾讯/巨量红线配置 + 华为商店投放时间豁免任务ID）。
 * 可选 label 行首标签：传入时切换为两行布局——label 与既有 chips 同行横排对齐，
 * 定宽输入框 + 「添加」独占下一行且与 label 左缘对齐（华为商店投放时间豁免任务ID
 * 用，避免 chips 较多时输入框被挤到 chips 行尾换行处）；不传保持单行 inline 布局
 * （既有调用点零改动）。同类先例：MultiDateChips 的 label prop。
 */
import { useState } from "react";

interface Props {
  ids: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
  /** 只读态：隐藏 × 与「添加」、输入框 readOnly；默认 false（既有调用点零改动）。 */
  disabled?: boolean;
  /** 行首标签（如「豁免任务ID」）：传入时切换两行布局——label 与 chips 同行、
   *  定宽输入框 + 「添加」在下一行与 label 左缘对齐；不传保持单行 inline 布局。 */
  label?: string;
}

export function IdChips({ ids, onChange, placeholder, disabled = false, label }: Props) {
  const [draft, setDraft] = useState("");

  function commit() {
    // 只读兜底：readOnly 不阻断 onKeyDown 的 Enter/逗号提交，须在此双保险
    if (disabled) return;
    const parts = draft
      .split(/[、,，\s]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    if (parts.length === 0) return;
    const next = [...ids];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    onChange(next);
    setDraft("");
  }

  function remove(id: string) {
    if (disabled) return;
    onChange(ids.filter((x) => x !== id));
  }

  // 三类节点抽为变量，供单行 inline / 两行（label）两种布局复用，行为完全一致
  const chipNodes = ids.map((id) => (
    <span key={id} className="leo-tag is-info">
      {id}
      {disabled ? null : (
        <button
          type="button"
          className="ml-1 text-text-muted hover:text-text-emphasis"
          onClick={() => remove(id)}
          aria-label={`移除 ${id}`}
        >
          ×
        </button>
      )}
    </span>
  ));

  const inputNode = (
    <input
      type="text"
      className="leo-input w-auto min-w-[8rem]"
      placeholder={placeholder ?? "输入 id，回车添加"}
      value={draft}
      readOnly={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === "," || e.key === "，") {
          e.preventDefault();
          commit();
        }
      }}
    />
  );

  const addButtonNode = disabled ? null : (
    <button type="button" className="leo-btn-ghost" onClick={commit}>
      添加
    </button>
  );

  // 两行布局（带 label）：label 与既有 chips 同行横排；输入框 + 「添加」在下一行
  // 与 label 左缘对齐（不与 chips 挤同一行）
  if (label) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="whitespace-nowrap text-[12px] text-text-muted">{label}</span>
          {chipNodes}
        </div>
        {/* 输入框定宽 10rem：不能直接给 input 挂 w-40——.leo-input 的 width:100%
            在 index.css 中定义于 @tailwind utilities 之后，同特异度后者胜，挂上去的
            w-* 会被吞（原 w-auto 自组件创建起即失效，输入框一直撑满整行、把
            「添加」挤到下一行）；改由外层定宽容器承接宽度，输入框 100% 填充容器，
            「添加」按钮紧随同行（items-center 横向对齐） */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-40">{inputNode}</div>
          {addButtonNode}
        </div>
      </div>
    );
  }

  // 单行 inline 布局（默认，既有调用点）：chips + 输入框 + 「添加」一行内 wrap
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chipNodes}
      {inputNode}
      {addButtonNode}
    </div>
  );
}
